import { useMutation, useQuery } from "@apollo/client";
import { parseError } from "apollo";
import Button from "components/base/Button";
import Errors from "components/base/Errors";
import { InlineSpinner } from "components/base/Spinner";
import { Alert } from "components/base/Toast";
import { useModals } from "ModalProvider";
import { Fragment } from "react";
import GearInventoryForm from "./GearInventoryForm";
import { FETCH_GEAR_SHIPMENTS, SAVE_GEAR_SHIPMENT } from "./graphql";

const InventoryView = ({ productId }) => {
  const { loading, error, data } = useQuery(FETCH_GEAR_SHIPMENTS, {
    variables: { productId },
  });
  const modal = useModals();

  const [saveShipment, saveShipmentRes] = useMutation(SAVE_GEAR_SHIPMENT, {
    onCompleted(res) {
      Alert("success", "Inventory record created.");
    },
    onError(error) {
      Alert("error", parseError(error));
    },
    refetchQueries: ["FETCH_GEAR_SHIPMENTS"],
  });

  function addHandler() {
    modal.present({
      title: "Add Inventory Record",
      center: true,
      children: <GearInventoryForm saveHandler={saveShipmentHandler} />,
    });
  }

  function editHandler(item) {
    modal.present({
      title: "Edit Inventory Record",
      center: true,
      children: (
        <GearInventoryForm
          initialValue={{
            id: item.id,
            warehouseName: item.shipment.warehouseName,
            date: new Date(item.shipment.date).toISOString().split("T")[0],
            inout: item.qty > 0 ? "IN" : "OUT",
            qty: Math.abs(item.qty),
            notes: item.shipment.notes,
          }}
          saveHandler={saveShipmentHandler}
        />
      ),
    });
  }

  function saveShipmentHandler(values) {
    modal.hide();
    saveShipment({
      variables: {
        productId,
        ...values,
        qty:
          values.inout === "IN" ? parseInt(values.qty) : -parseInt(values.qty),
      },
    });
  }

  if (loading)
    return (
      <div className="relative">
        <InlineSpinner />
      </div>
    );

  if (error) return <Errors error={error} />;

  const shipments = data.gearShipments;

  const warehouses = shipments.reduce((acc, item) => {
    const name = item.shipment.warehouseName;
    if (!acc[name]) {
      acc[name] = 0;
    }
    acc[name] += item.qty;
    return acc;
  }, {});

  return (
    <div className="card">
      <div className="flex items-center justify-between space-x-6">
        <h5>Inventory</h5>
        <div>
          <Button onClick={addHandler} loading={saveShipmentRes.loading}>
            + Add Record
          </Button>
        </div>
      </div>

      <div className="mt-2">
        {shipments.length === 0 && (
          <div className="text-gray-500">No inventory records found.</div>
        )}

        {shipments.length > 0 && (
          <div className="-mx-2 mt-2 whitespace-nowrap">
            <table>
              <tbody>
                {Object.keys(warehouses).map((name) => (
                  <Fragment key={name}>
                    <tr className="bg-gray-200 font-semibold">
                      <td colSpan={2}>{name}</td>
                      <td className="text-right">{warehouses[name]}</td>
                    </tr>
                    {shipments
                      .filter((s) => s.shipment.warehouseName === name)
                      .map((item) => (
                        <tr
                          key={item.id}
                          className="border-y border-gray-100 dark:border-gray-700"
                        >
                          <td>
                            <Button onClick={() => editHandler(item)}>
                              {
                                new Date(item.shipment.date)
                                  .toISOString()
                                  .split("T")[0]
                              }
                            </Button>
                          </td>

                          <td className="w-full whitespace-pre-wrap">
                            {item.shipment.notes}
                          </td>
                          <td className="text-right">{item.qty}</td>
                        </tr>
                      ))}
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default InventoryView;
